/**
 * ocean_patch_math — the PURE half of the one-patch FFT ocean (shader gaps R5, 2026-09-30). No three.js: every number the
 * GPU pipeline (OceanPatch.ts) uses is built here, and the CPU mirror that answers gameplay height queries reads the
 * SAME arrays, so the harness can pin CPU ≡ GPU (the GPU algorithm is emulated here texel for texel).
 *
 * THE PATCH. One N² grid over a P-metre square (default 256² over 512 m: 2 m texels). Texel (x, y) ↔ wavevector
 * k = 2π(x − N/2, y − N/2)/P (fft-pure's convention). Two BANDS, both a sub-disc of the grid:
 *   · the NORMAL band   λ ≥ `minWavelength` (default 8 m — the spectrum-baked detail maps own everything shorter, so
 *                       the energy is never counted twice), and
 *   · the VERTEX band   λ ≥ `vertexWavelength` (default 20 m = 2.5 × the sea's 8 m vertex spacing — THE WAVE/GRID
 *                       LAW: a displacement grid folds every wave under its Nyquist limit into per-quad tile noise).
 * The mesh displaces the vertex band only; the fragment reads the normal band's slopes in two sub-bands (vertex band,
 * the 8–20 m rest) so the distance filter can fold each into roughness (Bruneton 2010) — never both into the normal.
 *
 * THE FIELD. h(x, t) = Σ_k c_k(t)·e^{ik·x}, c_k(t) = h0(k)·e^{iωt} + conj(h0(−k))·e^{−iωt} (Tessendorf 2001 eq. 26 —
 * Hermitian, so h is real). ω = √(g|k|) (or tanh-depth) QUANTISED to multiples of 2π/T (Tessendorf's "looping in time"),
 * so the field repeats every T seconds: Atlas's water clock wraps at 2π·1000 s and the FFT sea never pops there.
 * Choppy horizontal displacement D̃ = +i·λ·k̂·h̃ (the SIGN: this pinches the crests — J = 1 − λ|k|A at a crest —
 * Tessendorf's x + λD with D = −ik̂h expands them; the trochoid, and every whitecap test, needs compression).
 *
 * THE CROSS-FADE. Every texel's two gaussians are fixed for the session; a sea-state change re-weights the per-texel
 * AMPLITUDES only (a new spectrum B beside the running A, h0 = mix(A, B, w) on both sides) — the waves grow and fade in
 * place, they never reshuffle, and nothing pops.
 *
 * ENERGY. The spectrum's SHAPE comes from the wind (JONSWAP by default — fetch-limited, peaked); its ENERGY is
 * normalised so the realisation's time-averaged spatial variance over the normal band equals `referenceVariance`
 * (default 0.1226 m² = the authored Gerstner sea's energy in the same band): at the typical wind the FFT sea carries the
 * Gerstner sea's energy, and the sea state's amplitude scale multiplies both the same way.
 */

import { dispersionOmega, generateButterfly, spectralDensity2D, type OceanSpectrumConfig } from './fft-pure.js';

const TAU = Math.PI * 2;
const GRAVITY = 9.81;

export interface OceanPatchConfig {
  /** Texels per side (power of two, 64..512). Default 256. */
  resolution?: number;
  /** World size of the patch (m). Default 512. */
  patchSize?: number;
  /** Shortest wavelength in the patch (m) — the detail maps own the rest. Default 8. */
  minWavelength?: number;
  /** Shortest wavelength the VERTEX band displaces (m). Default 20 (2.5 × an 8 m grid). */
  vertexWavelength?: number;
  /** Horizontal chop λ. Default 1. */
  choppiness?: number;
  /** ω is quantised to 2π/T so the field repeats every T s. Default 2π·1000 (Atlas's clock wrap). */
  loopPeriodS?: number;
  /** Time-averaged height variance of the whole band at amplitude scale 1 (m²). Default 0.1226. */
  referenceVariance?: number;
  /** Deterministic gaussians. Default 1789. */
  seed?: number;
  /** Finite depth (m) for the dispersion; default deep water. */
  depth?: number;
}

export interface OceanSpectrumParams {
  /** U10 (m/s) — the spectrum's shape (peak wavelength grows ~U²). */
  windSpeed: number;
  /** Heading (deg, 0 = +X, toward +Z positive — GerstnerWaves' convention). */
  windDirectionDeg: number;
  fetchKm?: number;
  model?: 'jonswap' | 'pierson-moskowitz' | 'phillips';
  gamma?: number;
}

/** The fixed per-texel data of a patch (built once per session). */
export interface PatchGrid {
  readonly N: number;
  readonly P: number;
  readonly dk: number;
  readonly texel: number;
  readonly minWavelength: number;
  readonly vertexWavelength: number;
  readonly chop: number;
  readonly loopPeriodS: number;
  readonly referenceVariance: number;
  readonly depth: number | undefined;
  /** Per texel: ω (quantised), kx, kz, band (0 = none, 1 = normal band only, 2 = vertex + normal) — the GPU's Ω texture. */
  readonly omegaData: Float32Array;
  /** Per texel: the two unit gaussians of k and the two of −k (g1, g2, g3, g4). */
  readonly gauss: Float32Array;
  /** Texels in the vertex band (half-plane representatives), for the CPU mirror. */
  readonly vertexHalf: Int32Array;
  /** Texels in the normal band (all). */
  readonly normalAll: Int32Array;
}

export const DEFAULT_PATCH: Required<Omit<OceanPatchConfig, 'depth'>> = {
  resolution: 256,
  patchSize: 512,
  minWavelength: 8,
  vertexWavelength: 20,
  choppiness: 1,
  loopPeriodS: TAU * 1000,
  referenceVariance: 0.1226,
  seed: 1789,
};

function lcg(s: number): number { return (Math.imul(s, 1664525) + 1013904223) >>> 0; }
/** A texel's two unit gaussians (Box–Muller over a coordinate hash — −k's gaussians are its OWN texel's). */
function gaussianAt(seed: number, x: number, y: number): [number, number] {
  let s = (seed ^ Math.imul(x + 1, 73856093) ^ Math.imul(y + 1, 19349663)) >>> 0;
  s = lcg(lcg(s));
  const u1 = Math.max(1e-9, s / 0xffffffff);
  s = lcg(s);
  const u2 = s / 0xffffffff;
  const r = Math.sqrt(-2 * Math.log(u1));
  return [r * Math.cos(TAU * u2), r * Math.sin(TAU * u2)];
}

/** The −k texel of texel (x, y) (the grid is centred at N/2; the Nyquist row maps onto itself). */
export function mirrorTexel(N: number, x: number, y: number): [number, number] {
  return [(N - x) % N, (N - y) % N];
}

export function buildPatchGrid(cfg: OceanPatchConfig = {}): PatchGrid {
  const rawN = cfg.resolution ?? DEFAULT_PATCH.resolution;
  const N = 2 ** Math.round(Math.log2(Math.min(512, Math.max(64, rawN))));
  const P = Math.max(16, cfg.patchSize ?? DEFAULT_PATCH.patchSize);
  const dk = TAU / P;
  const minW = Math.max(2 * (P / N), cfg.minWavelength ?? DEFAULT_PATCH.minWavelength); // never under 2 texels
  const vertW = Math.max(minW, cfg.vertexWavelength ?? DEFAULT_PATCH.vertexWavelength);
  const kN = TAU / minW, kV = TAU / vertW;
  const T = Math.max(1, cfg.loopPeriodS ?? DEFAULT_PATCH.loopPeriodS);
  const w0 = TAU / T;
  const seed = (cfg.seed ?? DEFAULT_PATCH.seed) >>> 0;
  const omegaData = new Float32Array(N * N * 4);
  const gauss = new Float32Array(N * N * 4);
  const vertexHalf: number[] = [];
  const normalAll: number[] = [];
  for (let y = 0; y < N; y++) {
    const kz = (y - N / 2) * dk;
    for (let x = 0; x < N; x++) {
      const kx = (x - N / 2) * dk;
      const k = Math.hypot(kx, kz);
      const i = y * N + x;
      const inN = k > 1e-9 && k <= kN * (1 + 1e-9) && x > 0 && y > 0; // the Nyquist row/column (x or y = 0) never carries energy
      const inV = inN && k <= kV * (1 + 1e-9);
      const band = inV ? 2 : inN ? 1 : 0;
      const w = k > 1e-9 ? Math.round(dispersionOmega(k, cfg.depth, GRAVITY) / w0) * w0 : 0;
      omegaData[i * 4] = w;
      omegaData[i * 4 + 1] = kx;
      omegaData[i * 4 + 2] = kz;
      omegaData[i * 4 + 3] = band;
      const [g1, g2] = gaussianAt(seed, x, y);
      const [mx, my] = mirrorTexel(N, x, y);
      const [g3, g4] = gaussianAt(seed, mx, my);
      gauss[i * 4] = g1; gauss[i * 4 + 1] = g2; gauss[i * 4 + 2] = g3; gauss[i * 4 + 3] = g4;
      if (inN) normalAll.push(i);
      if (inV && (y > N / 2 || (y === N / 2 && x > N / 2))) vertexHalf.push(i);
    }
  }
  return {
    N, P, dk, texel: P / N, minWavelength: minW, vertexWavelength: vertW, chop: cfg.choppiness ?? DEFAULT_PATCH.choppiness,
    loopPeriodS: T, referenceVariance: cfg.referenceVariance ?? DEFAULT_PATCH.referenceVariance, depth: cfg.depth,
    omegaData, gauss, vertexHalf: Int32Array.from(vertexHalf), normalAll: Int32Array.from(normalAll),
  };
}

/**
 * The h0 texture for a spectrum: per texel (h0(k).re, h0(k).im, h0(−k).re, h0(−k).im), band-masked, normalised so the
 * realisation's time-averaged variance over the normal band is the grid's `referenceVariance`. Float32 (the GPU's exact
 * input — the CPU mirror reads the same array).
 */
export function buildH0(grid: PatchGrid, params: OceanSpectrumParams): Float32Array {
  const { N, dk } = grid;
  const cfg: OceanSpectrumConfig = {
    windSpeed: params.windSpeed, windDirectionDeg: params.windDirectionDeg, fetchKm: params.fetchKm ?? 60,
    model: params.model ?? 'jonswap', gamma: params.gamma ?? 3.3, depth: grid.depth,
  };
  const amp = (kx: number, kz: number): number => Math.sqrt(Math.max(0, spectralDensity2D(kx, kz, cfg) * dk * dk) / 2);
  const out = new Float32Array(N * N * 4);
  let variance = 0;
  for (const i of grid.normalAll) {
    const kx = grid.omegaData[i * 4 + 1], kz = grid.omegaData[i * 4 + 2];
    const a = amp(kx, kz), am = amp(-kx, -kz);
    const o = i * 4;
    out[o] = grid.gauss[o] * a; out[o + 1] = grid.gauss[o + 1] * a;
    out[o + 2] = grid.gauss[o + 2] * am; out[o + 3] = grid.gauss[o + 3] * am;
    variance += out[o] ** 2 + out[o + 1] ** 2 + out[o + 2] ** 2 + out[o + 3] ** 2;
  }
  // Time-averaged spatial variance = Σ_k (|h0(k)|² + |h0(−k)|²) over every texel (Parseval; the cross term averages out).
  const s = variance > 0 ? Math.sqrt(grid.referenceVariance / variance) : 0;
  for (let i = 0; i < out.length; i++) out[i] *= s;
  return out;
}

/** Linear blend of two h0 grids (the cross-fade: same gaussians, amplitudes re-weighted). */
export function blendH0(a: Float32Array, b: Float32Array, w: number, out: Float32Array): Float32Array {
  const u = 1 - w;
  for (let i = 0; i < out.length; i++) out[i] = a[i] * u + b[i] * w;
  return out;
}

// ── Statistics (for the uniforms and the harness) ───────────────────────────────────────────────────────────────────

export interface PatchStats {
  /** Time-averaged height variance of the vertex band / the whole normal band (m², amplitude scale 1). */
  varVertex: number;
  varNormal: number;
  /** Mean-square slope (both axes) of the vertex band and of the short band (λ in [min, vertex)). */
  slopeVarLow: number;
  slopeVarShort: number;
}

/** Stats of h0 blended at w (A and B share gaussians, so everything is a quadratic in w — computed directly). */
export function patchStats(grid: PatchGrid, h0: Float32Array): PatchStats {
  let vV = 0, vN = 0, sL = 0, sS = 0;
  for (const i of grid.normalAll) {
    const o = i * 4;
    const e = h0[o] ** 2 + h0[o + 1] ** 2 + h0[o + 2] ** 2 + h0[o + 3] ** 2;
    const kx = grid.omegaData[o + 1], kz = grid.omegaData[o + 2];
    const k2 = kx * kx + kz * kz;
    vN += e;
    if (grid.omegaData[o + 3] > 1.5) { vV += e; sL += e * k2; } else sS += e * k2;
  }
  return { varVertex: vV, varNormal: vN, slopeVarLow: sL, slopeVarShort: sS };
}

// ── The CPU mirror: per-time coefficients and the mode sum ──────────────────────────────────────────────────────────

/** One vertex-band mode (a half-plane representative) at one time: the coefficient c_k(t) and its wavevector. */
export interface ModeSet {
  count: number;
  kx: Float64Array; kz: Float64Array; kxN: Float64Array; kzN: Float64Array;
  /** c_k(t) (real, imaginary) — Σ over the half plane of 2·Re(c·e^{ik·x}) is the field. */
  cRe: Float64Array; cIm: Float64Array;
  /** The L1 bound on what the pruned modes could add to the height (m, amplitude scale 1): 2·Σ(|h0(k)| + |h0(−k)|). */
  prunedTailH: number;
  /** The same for a horizontal component (× λ). */
  prunedTailD: number;
  /** Modes kept / considered. */
  kept: number; considered: number;
}

/**
 * The vertex-band modes worth summing for h0, pruned so the discarded ones can add at most `tailM` metres to the height
 * (a rigorous L1 bound — each pruned mode's height contribution is ≤ 2(|h0(k)| + |h0(−k)|) at any x and t).
 * `order` is the kept texel list (largest bound first) — time-independent, so the caller caches it per h0.
 */
export function pruneVertexModes(grid: PatchGrid, h0: Float32Array, tailM: number): { order: Int32Array; tail: number } {
  const list = Array.from(grid.vertexHalf);
  const bound = (i: number): number => { const o = i * 4; return Math.hypot(h0[o], h0[o + 1]) + Math.hypot(h0[o + 2], h0[o + 3]); };
  list.sort((a, b) => bound(b) - bound(a));
  let tail = 0;
  let keep = list.length;
  // Drop from the smallest end while the dropped tail stays under the budget.
  for (let j = list.length - 1; j >= 0; j--) {
    const t = tail + 2 * bound(list[j]);
    if (t > tailM) break;
    tail = t;
    keep = j;
  }
  return { order: Int32Array.from(list.slice(0, keep)), tail };
}

/** c_k(t) for the kept modes (float64 phases, the float32 inputs the GPU reads). */
export function modesAt(grid: PatchGrid, h0: Float32Array, order: Int32Array, t: number, tail: number, into?: ModeSet): ModeSet {
  const n = order.length;
  const m: ModeSet = into && into.count === n ? into : {
    count: n, kx: new Float64Array(n), kz: new Float64Array(n), kxN: new Float64Array(n), kzN: new Float64Array(n),
    cRe: new Float64Array(n), cIm: new Float64Array(n), prunedTailH: 0, prunedTailD: 0, kept: n, considered: grid.vertexHalf.length,
  };
  for (let j = 0; j < n; j++) {
    const i = order[j];
    const o = i * 4;
    const w = grid.omegaData[o], kx = grid.omegaData[o + 1], kz = grid.omegaData[o + 2];
    const k = Math.hypot(kx, kz);
    const c = Math.cos(w * t), s = Math.sin(w * t);
    const a = h0[o], b = h0[o + 1], p = h0[o + 2], q = h0[o + 3];
    m.kx[j] = kx; m.kz[j] = kz; m.kxN[j] = kx / k; m.kzN[j] = kz / k;
    m.cRe[j] = a * c - b * s + p * c - q * s;
    m.cIm[j] = a * s + b * c - p * s - q * c;
  }
  m.prunedTailH = tail;
  m.prunedTailD = tail * grid.chop;
  m.kept = n;
  m.considered = grid.vertexHalf.length;
  return m;
}

export interface FieldSample {
  /** Displacement (m): horizontal chop and height. */
  dx: number; dy: number; dz: number;
  /** Slopes ∂h/∂x, ∂h/∂z. */
  sx: number; sz: number;
  /** Horizontal-map derivatives ∂Dx/∂x, ∂Dz/∂z, ∂Dx/∂z. */
  jxx: number; jzz: number; jxz: number;
}

/** The vertex-band field at a patch coordinate (x, z) — metres, reduced mod P by the caller or not (phases are float64). */
export function sampleModes(m: ModeSet, chop: number, x: number, z: number, out: FieldSample): FieldSample {
  let h = 0, dx = 0, dz = 0, sx = 0, sz = 0, jxx = 0, jzz = 0, jxz = 0;
  for (let j = 0; j < m.count; j++) {
    const th = m.kx[j] * x + m.kz[j] * z;
    const c = Math.cos(th), s = Math.sin(th);
    const Q = m.cRe[j] * c - m.cIm[j] * s;
    const R = m.cIm[j] * c + m.cRe[j] * s;
    const kxN = m.kxN[j], kzN = m.kzN[j], kx = m.kx[j], kz = m.kz[j];
    h += Q;
    dx += kxN * R; dz += kzN * R;
    sx += kx * R; sz += kz * R;
    jxx += kx * kxN * Q; jzz += kz * kzN * Q; jxz += kz * kxN * Q;
  }
  out.dy = 2 * h;
  out.dx = -2 * chop * dx; out.dz = -2 * chop * dz;
  out.sx = -2 * sx; out.sz = -2 * sz;
  out.jxx = -2 * chop * jxx; out.jzz = -2 * chop * jzz; out.jxz = -2 * chop * jxz;
  return out;
}

/**
 * THE MEAN LEVEL. The Eulerian mean of a choppy surface sits BELOW the still level: over the periodic patch
 * ⟨h·∇·D⟩ = −λ·Σ_k |k|·|c_k(t)|² exactly (Parseval; D̃ = iλk̂h̃ ⇒ ∇·D̃ = −λ|k|h̃), the mean of h itself is 0 (no k = 0
 * mode) and the next term is third order. Lifting the drawn surface by this puts the MEAN WATER LEVEL on the sea level.
 * Summed over EVERY vertex-band mode (not the pruned set) — at amplitude scale 1; the caller multiplies by s².
 */
export function meanLiftAt(grid: PatchGrid, h0: Float32Array, t: number): number {
  let sum = 0;
  for (const i of grid.vertexHalf) {
    const o = i * 4;
    const w = grid.omegaData[o], kx = grid.omegaData[o + 1], kz = grid.omegaData[o + 2];
    const c = Math.cos(w * t), s = Math.sin(w * t);
    const a = h0[o], b = h0[o + 1], p = h0[o + 2], q = h0[o + 3];
    const re = a * c - b * s + p * c - q * s;
    const im = a * s + b * c - p * s - q * c;
    sum += 2 * Math.hypot(kx, kz) * (re * re + im * im); // the half plane: each pair (k, −k) counted twice
  }
  return grid.chop * sum;
}

// ── The GPU pipeline, emulated (the harness pins CPU mirror ≡ this ≡ the GPU readback) ──────────────────────────────

/**
 * The evolve kernel's output for one texel: the three packed spectra (P0, P1, P2 — two complex each), exactly the
 * arithmetic of OceanPatch's evolve kernel (float64 here). The pack: C = F + i·G ⇒ (F.re − G.im, F.im + G.re).
 */
export function evolveTexel(grid: PatchGrid, h0: Float32Array, i: number, t: number, out: Float64Array): void {
  const o = i * 4;
  const w = grid.omegaData[o], kx = grid.omegaData[o + 1], kz = grid.omegaData[o + 2], band = grid.omegaData[o + 3];
  const mV = band > 1.5 ? 1 : 0, mN = band > 0.5 ? 1 : 0;
  const c = Math.cos(w * t), s = Math.sin(w * t);
  const a = h0[o], b = h0[o + 1], p = h0[o + 2], q = h0[o + 3];
  const hRe = a * c - b * s + p * c - q * s;
  const hIm = a * s + b * c - p * s - q * c;
  const k = Math.max(1e-6, Math.hypot(kx, kz));
  const kxN = kx / k, kzN = kz / k, lam = grid.chop;
  // i·f·h (f real) = (−f·hIm, f·hRe); f·h = (f·hRe, f·hIm)
  const hv = [hRe * mV, hIm * mV];
  const dxv = [-lam * kxN * hIm * mV, lam * kxN * hRe * mV];
  const dzv = [-lam * kzN * hIm * mV, lam * kzN * hRe * mV];
  const sxf = [-kx * hIm * mN, kx * hRe * mN];
  const szf = [-kz * hIm * mN, kz * hRe * mN];
  const sxl = [-kx * hIm * mV, kx * hRe * mV];
  const szl = [-kz * hIm * mV, kz * hRe * mV];
  const fxx = -lam * kx * kxN * mN, fzz = -lam * kz * kzN * mN, fxz = -lam * kz * kxN * mN;
  const jxx = [fxx * hRe, fxx * hIm], jzz = [fzz * hRe, fzz * hIm], jxz = [fxz * hRe, fxz * hIm];
  const hf = [hRe * mN, hIm * mN];
  const pack = (f: number[], g: number[], at: number): void => { out[at] = f[0] - g[1]; out[at + 1] = f[1] + g[0]; };
  pack(hv, dxv, 0); pack(dzv, sxf, 2);
  pack(szf, sxl, 4); pack(szl, jxx, 6);
  pack(jzz, jxz, 8); pack(hf, [0, 0], 10);
}

/**
 * The whole pipeline on the CPU: evolve → 2·log2(N) butterfly passes (fft-pure's tables, the GPU's own) → the
 * (−1)^(x+y) recentre → the unpacked fields per texel: disp (dx, dy, dz), deriv (sxF, szF, sxL, szL), jac (jxx, jzz, jxz, hF).
 * Slow (float64, N² · 12 channels) — tests only.
 */
export function emulatePipeline(grid: PatchGrid, h0: Float32Array, t: number): { disp: Float64Array; deriv: Float64Array; jac: Float64Array } {
  const { N } = grid;
  const C = 12;
  let a = new Float64Array(N * N * C);
  const tmp = new Float64Array(C);
  for (let i = 0; i < N * N; i++) { evolveTexel(grid, h0, i, t, tmp); a.set(tmp, i * C); }
  const { data: bf, stages } = generateButterfly(N);
  let b = new Float64Array(N * N * C);
  for (let axis = 0; axis < 2; axis++) {
    for (let stage = 0; stage < stages; stage++) {
      for (let y = 0; y < N; y++) {
        for (let x = 0; x < N; x++) {
          const lane = axis === 0 ? x : y;
          const o = (stage * N + lane) * 4;
          const wr = bf[o], wi = bf[o + 1], ia = bf[o + 2], ib = bf[o + 3];
          const A = axis === 0 ? (y * N + ia) * C : (ia * N + x) * C;
          const B = axis === 0 ? (y * N + ib) * C : (ib * N + x) * C;
          const D = (y * N + x) * C;
          for (let ch = 0; ch < C; ch += 2) {
            b[D + ch] = a[A + ch] + (wr * a[B + ch] - wi * a[B + ch + 1]);
            b[D + ch + 1] = a[A + ch + 1] + (wr * a[B + ch + 1] + wi * a[B + ch]);
          }
        }
      }
      const s = a; a = b; b = s;
    }
  }
  const disp = new Float64Array(N * N * 3), deriv = new Float64Array(N * N * 4), jac = new Float64Array(N * N * 4);
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    const i = y * N + x, o = i * C, sg = (x + y) % 2 === 0 ? 1 : -1;
    disp[i * 3] = a[o + 1] * sg; disp[i * 3 + 1] = a[o] * sg; disp[i * 3 + 2] = a[o + 2] * sg;
    deriv[i * 4] = a[o + 3] * sg; deriv[i * 4 + 1] = a[o + 4] * sg; deriv[i * 4 + 2] = a[o + 5] * sg; deriv[i * 4 + 3] = a[o + 6] * sg;
    jac[i * 4] = a[o + 7] * sg; jac[i * 4 + 1] = a[o + 8] * sg; jac[i * 4 + 2] = a[o + 9] * sg; jac[i * 4 + 3] = a[o + 10] * sg;
  }
  return { disp, deriv, jac };
}

// ── Fast CPU transforms on a coarse lattice (the drawn query's vertices, the foam grid's Jacobian) ────────────────

interface FftTables { rev: Uint32Array; cos: Float64Array; sin: Float64Array }
const FFT_TABLES = new Map<number, FftTables>();
function fftTables(M: number): FftTables {
  let t = FFT_TABLES.get(M);
  if (!t) {
    const bits = Math.log2(M);
    const rev = new Uint32Array(M);
    for (let i = 0; i < M; i++) { let r = 0; for (let b = 0; b < bits; b++) r = (r << 1) | ((i >> b) & 1); rev[i] = r; }
    const cos = new Float64Array(M / 2), sin = new Float64Array(M / 2);
    for (let k = 0; k < M / 2; k++) { cos[k] = Math.cos((TAU * k) / M); sin[k] = Math.sin((TAU * k) / M); }
    t = { rev, cos, sin };
    FFT_TABLES.set(M, t);
  }
  return t;
}
/** In-place unnormalised inverse DFT (e^{+iθ}) of a strided run of M complex values (iterative radix-2). */
function ifftRun(re: Float64Array, im: Float64Array, off: number, stride: number, M: number, T: FftTables, bre: Float64Array, bim: Float64Array): void {
  for (let i = 0; i < M; i++) { const j = off + T.rev[i] * stride; bre[i] = re[j]; bim[i] = im[j]; }
  for (let size = 2; size <= M; size <<= 1) {
    const half = size >> 1, step = M / size;
    for (let i = 0; i < M; i += size) {
      for (let j = 0; j < half; j++) {
        const wr = T.cos[j * step], wi = T.sin[j * step];
        const a = i + j, b = a + half;
        const tr = wr * bre[b] - wi * bim[b], ti = wr * bim[b] + wi * bre[b];
        bre[b] = bre[a] - tr; bim[b] = bim[a] - ti;
        bre[a] += tr; bim[a] += ti;
      }
    }
  }
  for (let i = 0; i < M; i++) { const j = off + i * stride; re[j] = bre[i]; im[j] = bim[i]; }
}
/** In-place 2-D inverse DFT of an M² complex grid (rows, then columns). */
export function ifft2InPlace(re: Float64Array, im: Float64Array, M: number): void {
  const T = fftTables(M);
  const bre = new Float64Array(M), bim = new Float64Array(M);
  for (let y = 0; y < M; y++) ifftRun(re, im, y * M, 1, M, T, bre, bim);
  for (let x = 0; x < M; x++) ifftRun(re, im, x, M, M, T, bre, bim);
}

/** Every texel of `list` whose wavenumber index fits a centred M² lattice (|n| < M/2 on both axes). */
function latticeIndex(grid: PatchGrid, list: Int32Array, M: number): { src: Int32Array; dst: Int32Array } {
  const src: number[] = [], dst: number[] = [];
  const N = grid.N, half = M / 2;
  for (const i of list) {
    const nx = (i % N) - N / 2, ny = ((i / N) | 0) - N / 2;
    if (Math.abs(nx) >= half || Math.abs(ny) >= half) continue;
    src.push(i); dst.push((ny + half) * M + (nx + half));
  }
  return { src: Int32Array.from(src), dst: Int32Array.from(dst) };
}
const LATTICE_INDEX = new WeakMap<PatchGrid, Map<string, { src: Int32Array; dst: Int32Array }>>();
function latticeFor(grid: PatchGrid, which: 'vertex' | 'normal', M: number): { src: Int32Array; dst: Int32Array } {
  let m = LATTICE_INDEX.get(grid);
  if (!m) { m = new Map(); LATTICE_INDEX.set(grid, m); }
  const key = `${which}:${M}`;
  let v = m.get(key);
  if (!v) {
    // The vertex band as ALL its texels (both half planes) — the lattice IFFT sums over the full plane.
    const list = which === 'normal' ? grid.normalAll : Int32Array.from(Array.from(grid.normalAll).filter((i) => grid.omegaData[i * 4 + 3] > 1.5));
    v = latticeIndex(grid, list, M);
    m.set(key, v);
  }
  return v;
}

/** True when the whole vertex band fits an M² lattice over the patch (no wavenumber folds). */
export function vertexBandFits(grid: PatchGrid, M: number): boolean {
  const nMax = grid.P / grid.vertexWavelength;
  return Number.isInteger(Math.log2(M)) && nMax < M / 2 - 1e-9;
}

/**
 * THE VERTEX LATTICE. The vertex band's displacement EXACTLY at the points x = X·(P/M) (X ∈ [0, M)²), by one small
 * CPU IFFT pair: every vertex-band mode, no pruning. When a drawn mesh's vertices sit on this lattice (spacing = P/M,
 * origin a multiple of it — Atlas's 8 m sea over a 512 m patch: M = 64), every vertex value the drawn query needs is a
 * lookup. Returns dx, dy, dz (amplitude scale 1, no lift).
 */
export function vertexLattice(grid: PatchGrid, h0: Float32Array, t: number, M: number): { M: number; dx: Float64Array; dy: Float64Array; dz: Float64Array } {
  const { src, dst } = latticeFor(grid, 'vertex', M);
  const n = M * M;
  const r1 = new Float64Array(n), i1 = new Float64Array(n), r2 = new Float64Array(n), i2 = new Float64Array(n);
  const lam = grid.chop;
  for (let q = 0; q < src.length; q++) {
    const o = src[q] * 4, j = dst[q];
    const w = grid.omegaData[o], kx = grid.omegaData[o + 1], kz = grid.omegaData[o + 2];
    const k = Math.max(1e-6, Math.hypot(kx, kz));
    const c = Math.cos(w * t), s = Math.sin(w * t);
    const a = h0[o], b = h0[o + 1], p = h0[o + 2], qq = h0[o + 3];
    const hRe = a * c - b * s + p * c - qq * s;
    const hIm = a * s + b * c - p * s - qq * c;
    // C1 = h + i·Dx with Dx̃ = iλk̂x·h̃ = (−λk̂x·hIm, λk̂x·hRe); C2 = Dz (+ i·0).
    const fx = lam * (kx / k), fz = lam * (kz / k);
    r1[j] = hRe - fx * hRe; i1[j] = hIm - fx * hIm; // (h.re − Dx.im, h.im + Dx.re) = (hRe − fx·hRe, hIm − fx·hIm)
    r2[j] = -fz * hIm; i2[j] = fz * hRe;
  }
  ifft2InPlace(r1, i1, M); ifft2InPlace(r2, i2, M);
  const dx = new Float64Array(n), dy = new Float64Array(n), dz = new Float64Array(n);
  for (let y = 0; y < M; y++) for (let x = 0; x < M; x++) {
    const i = y * M + x, sg = (x + y) % 2 === 0 ? 1 : -1;
    dy[i] = r1[i] * sg; dx[i] = i1[i] * sg; dz[i] = r2[i] * sg;
  }
  return { M, dx, dy, dz };
}

/**
 * THE DRAWN MEAN LEVEL. The Eulerian mean height of the triangle mesh a lattice-aligned grid draws (three's PlaneGeometry
 * split: (a, b, d) under the b–d diagonal, (b, c, d) over it), over one lattice period, at amplitude scale s and no lift:
 * Σ area·(mean vertex height) / Σ area with each triangle's DISPLACED (signed) XZ area. The continuous surface drops by
 * `meanLiftAt`; a mesh on an 8 m grid resolves the horizontal compression of a ≥ 20 m band only partly (its drop is ~⅔
 * of the continuous one — measured on WebGPU: −18.7 mm against the continuous −27.7), so the drawn sea's lift is THIS.
 */
export function latticeMeshMean(L: { M: number; dx: Float64Array; dy: Float64Array; dz: Float64Array }, spacing: number, s: number): number {
  const { M } = L;
  let aSum = 0, hSum = 0;
  const vx = (i: number, j: number): number => i * spacing + s * L.dx[(j % M) * M + (i % M)];
  const vz = (i: number, j: number): number => j * spacing + s * L.dz[(j % M) * M + (i % M)];
  const vy = (i: number, j: number): number => s * L.dy[(j % M) * M + (i % M)];
  const tri = (i0: number, j0: number, i1: number, j1: number, i2: number, j2: number): void => {
    const x0 = vx(i0, j0), z0 = vz(i0, j0), x1 = vx(i1, j1), z1 = vz(i1, j1), x2 = vx(i2, j2), z2 = vz(i2, j2);
    const a = -0.5 * ((x1 - x0) * (z2 - z0) - (z1 - z0) * (x2 - x0)); // positive for the undisplaced PlaneGeometry winding
    aSum += a;
    hSum += a * (vy(i0, j0) + vy(i1, j1) + vy(i2, j2)) / 3;
  };
  for (let j = 0; j < M; j++) for (let i = 0; i < M; i++) {
    tri(i, j, i, j + 1, i + 1, j);         // (a, b, d)
    tri(i, j + 1, i + 1, j + 1, i + 1, j); // (b, c, d)
  }
  return hSum / aSum;
}

/**
 * Jxx, Jzz, Jxz of the modes with |n| < M/2 (λ ≥ 2P/M) on an M² grid over the patch (one CPU IFFT pair): the coarse
 * whitecap source for a CPU foam grid whose cells are ~24 m anyway. Periodic in P.
 */
export function jacobianGrid(grid: PatchGrid, h0: Float32Array, t: number, M = 64): { M: number; jxx: Float64Array; jzz: Float64Array; jxz: Float64Array } {
  const { src, dst } = latticeFor(grid, 'normal', M);
  const n = M * M;
  const r1 = new Float64Array(n), i1 = new Float64Array(n), r2 = new Float64Array(n), i2 = new Float64Array(n);
  const lam = grid.chop;
  for (let q = 0; q < src.length; q++) {
    const o = src[q] * 4, j = dst[q];
    const w = grid.omegaData[o], kx = grid.omegaData[o + 1], kz = grid.omegaData[o + 2];
    const k = Math.max(1e-6, Math.hypot(kx, kz));
    const c = Math.cos(w * t), s = Math.sin(w * t);
    const a = h0[o], b = h0[o + 1], p = h0[o + 2], qq = h0[o + 3];
    const hRe = a * c - b * s + p * c - qq * s;
    const hIm = a * s + b * c - p * s - qq * c;
    const fxx = -lam * kx * (kx / k), fzz = -lam * kz * (kz / k), fxz = -lam * kz * (kx / k);
    // C1 = Jxx + i·Jzz: (Fxx.re − Fzz.im, Fxx.im + Fzz.re); C2 = Jxz.
    r1[j] = fxx * hRe - fzz * hIm; i1[j] = fxx * hIm + fzz * hRe;
    r2[j] = fxz * hRe; i2[j] = fxz * hIm;
  }
  ifft2InPlace(r1, i1, M); ifft2InPlace(r2, i2, M);
  const jxx = new Float64Array(n), jzz = new Float64Array(n), jxz = new Float64Array(n);
  for (let y = 0; y < M; y++) for (let x = 0; x < M; x++) {
    const i = y * M + x, sg = (x + y) % 2 === 0 ? 1 : -1;
    jxx[i] = r1[i] * sg; jzz[i] = i1[i] * sg; jxz[i] = r2[i] * sg;
  }
  return { M, jxx, jzz, jxz };
}

/** Bilinear read of a periodic M² grid covering P metres at patch coordinate (x, z). */
export function periodicBilinear(v: Float64Array, M: number, P: number, x: number, z: number): number {
  const u = (((x / P) % 1) + 1) % 1 * M, w = (((z / P) % 1) + 1) % 1 * M;
  const x0 = Math.floor(u), z0 = Math.floor(w);
  const fx = u - x0, fz = w - z0;
  const x1 = (x0 + 1) % M, z1 = (z0 + 1) % M;
  const a = v[z0 * M + x0], b = v[z0 * M + x1], c = v[z1 * M + x0], d = v[z1 * M + x1];
  return (a * (1 - fx) + b * fx) * (1 - fz) + (c * (1 - fx) + d * fx) * fz;
}

/** A value reduced into [0, P) in float64 (the patch coordinate of an absolute position). */
export function patchCoord(x: number, P: number): number {
  const r = x % P;
  return r < 0 ? r + P : r;
}
